<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFieldArray, useForm } from 'vee-validate';
import { useMutation, useQuery } from '@tanstack/vue-query';
import * as yup from 'yup';
import * as rutHelpers from 'rut-helpers';
import {
  patientsApi,
  type PatientForm,
  type Patient,
  type PatientAddressForm,
  type ServiceAssignmentPayload,
  type ArchivedPatientConflict,
} from '@/api/patients';
import { patientServiceCategoriesApi } from '@/api/patient-service-categories';
import { todayIso } from '@/utils/calendar-events';
import { type SerializedUser } from '@/api/users';
import type { AxiosError } from 'axios';
import type { RepresentativeForm } from '@/api/representatives';
import GoBackButton from '@/components/go-back-button.vue';
import PatientRepresentativesForm from '@/components/patients/patient-representatives-form.vue';
import PatientAddressesForm from '@/components/patients/patient-addresses-form.vue';
import GroupMembershipPicker from '@/components/groups/group-membership-picker.vue';
import { useGroupMembershipPicker } from '@/composables/useGroupMembershipPicker';
import { isNumber } from '@/utils/number-functions';
import { usePermissions } from '@/composables/usePermissions';
import { formatShortDate } from '@/utils/patient-status';
import { authorIdentityErrorText } from '@/utils/author-identity';

const { t } = useI18n();
const MIN_BIRTH_YEAR = 1900;
const MAX_WEIGHT = 350;
const MIN_WEIGHT = 1;

const currentUser = inject<Ref<SerializedUser>>('currentUser');

interface Props {
  patient: PatientForm;
  mode: 'new' | 'edit';
}

const props = defineProps<Props>();

const validationSchema = yup.object({
  firstName: yup.string().required(t('patientForm.fieldRequired')),
  lastName: yup.string().required(t('patientForm.fieldRequired')),
  dni: yup.string().nullable()
    .test('valid-dni', t('patientForm.invalidDni'), (value) => (value ? rutHelpers.rutValidate(value) : true)),
  weight: yup.number()
    .nullable()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .test('weight-range', t('patientForm.weightRequired'), (value) => {
      if (value === null || value === undefined) return true;

      return value >= MIN_WEIGHT && value <= MAX_WEIGHT;
    }),
  birthdate: yup.date()
    .nullable()
    .transform((value) => (value === '' || Number.isNaN(Date.parse(value)) ? null : value))
    .test('birthdate-range', t('patientForm.birthdateRequired'), (value) => {
      if (value === null || value === undefined) return true;

      return value >= new Date(MIN_BIRTH_YEAR, 0, 1) && value <= new Date();
    }),
  endsOn: yup.date()
    .nullable()
    .transform((value) => (value === '' || Number.isNaN(Date.parse(value)) ? null : value))
    .test('ends-after-starts', t('patientForm.endsOnMustBeAfterStartsOn'), function (value) {
      if (!value) return true;
      const { startsOn } = this.parent;
      if (!startsOn) return true;

      return new Date(value) >= new Date(startsOn);
    }),
  representativesAttributes: yup.array()
    .min(1, t('patientForm.atLeastOneRepresentative'))
    .test('hasActiveRepresentatives', t('patientForm.atLeastOneRepresentative'), (value) => {
      if (!value) return false;

      return value.some((representative) => !representative._destroy); // eslint-disable-line no-underscore-dangle
    })
    .of(
      yup.object({
        firstName: yup.string().required(t('patientForm.fieldRequired')),
        lastName: yup.string().required(t('patientForm.fieldRequired')),
        email: yup.string().email(t('patientForm.emailFormat')).nullable(),
        dni: yup.string().nullable()
          .test('valid-dni', t('patientForm.invalidDni'), (value) => (value ? rutHelpers.rutValidate(value) : true)),
        _destroy: yup.boolean(),
      }),
    ),
  patientAddressesAttributes: yup.array()
    .test('singlePrimary', t('patientForm.singlePrimaryAddress'), (value) => {
      if (!value) return true;
      const active = value.filter((address) => !address._destroy); // eslint-disable-line no-underscore-dangle
      if (active.length === 0) return true;

      return active.filter((address) => address.primary).length === 1;
    })
    .of(
      yup.object({
        address: yup.string().nullable()
          .test('has-coordinates', t('patientForm.addressRequiresLocation'), function (value) {
            if (!value) return true;
            const { latitude, longitude } = this.parent;

            return !!(latitude && longitude);
          }),
        _destroy: yup.boolean(),
      }),
    ),
});

// The serialized patient still carries resolved patient-level address fields for display;
// the form must not echo those deprecated fields back on submit.
function toInitialValues(patient: PatientForm): PatientForm {
  const values = { ...patient } as PatientForm & Record<string, unknown>;
  delete values.address;
  delete values.latitude;
  delete values.longitude;
  // Who receives the billing emails lives on the patient, not the representative.
  const billingIds = patient.billingRepresentativeIds ?? [];
  const initialRepresentatives = values.representativesAttributes as unknown as RepresentativeForm[] | undefined;
  if (Array.isArray(initialRepresentatives)) {
    (values as Record<string, unknown>).representativesAttributes = initialRepresentatives.map((representative) => ({
      ...representative, receivesCharges: billingIds.includes(Number(representative.id)),
    }));
  }
  delete values.billingRepresentativeIds;
  return values;
}

const initialValues = toInitialValues(props.patient);

const { handleSubmit, values, errors, defineField } = useForm<PatientForm>({ validationSchema, initialValues });

const [idCode, idCodeAttrs] = defineField('idCode');
const [firstName, firstNameAttrs] = defineField('firstName');
const [lastName, lastNameAttrs] = defineField('lastName');
const [nickname, nicknameAttrs] = defineField('nickname');
const [dni, dniAttrs] = defineField('dni');
const [passportNumber, passportNumberAttrs] = defineField('passportNumber');
const [birthdate, birthdateAttrs] = defineField('birthdate');
const [weight, weightAttrs] = defineField('weight');
const [sex, sexAttrs] = defineField('sex');
const [medicalHistory, medicalHistoryAttrs] = defineField('medicalHistory');
const [surgicalHistory, surgicalHistoryAttrs] = defineField('surgicalHistory');
const [technicalAids, technicalAidsAttrs] = defineField('technicalAids');
const [insurances, insurancesAttrs] = defineField('insurances');
const [comments, commentsAttrs] = defineField('comments');
const [startsOn, startsOnAttrs] = defineField('startsOn');
const [endsOn, endsOnAttrs] = defineField('endsOn');
const { remove, push, fields: representatives } = useFieldArray<RepresentativeForm>('representativesAttributes');
const {
  remove: removeAddress,
  push: pushAddress,
  fields: addresses,
} = useFieldArray<PatientAddressForm>('patientAddressesAttributes');

watch(dni, (value) => {
  dni.value = rutHelpers.rutFormat(value);
}, { immediate: true });

const submitButtonDisabled = ref(false);

const {
  showGroupPicker,
  mustPickGroup,
  selectedGroupIds,
  groupPickError,
  groupPickerHint,
  groupItems,
  validate: validateGroupPick,
} = useGroupMembershipPicker(
  props.mode, 'patient', (props.patient as Partial<Patient>)?.groupIds ?? [],
);

const serviceCategoriesEnabled = currentUser?.value?.companyPatientServiceCategoriesEnabled ?? false;
const statusEnabled = currentUser?.value?.companyCarePlanningEnabled ?? false;
const { hasPermission } = usePermissions();

const { data: serviceCategories } = useQuery({
  queryKey: ['patientServiceCategories', 'forFilters'],
  queryFn: patientServiceCategoriesApi.indexForFilters,
  enabled: serviceCategoriesEnabled,
  initialData: [],
});

const initialAssignment = (props.patient as Partial<Patient>)?.currentServiceAssignment ?? null;
const selectedCategoryId = ref<number | null>(initialAssignment?.categoryId ?? null);
const selectedSubcategoryId = ref<number | null>(initialAssignment?.subcategoryId ?? null);
const assignmentStartsOn = ref<string>(todayIso());

watch(selectedCategoryId, () => {
  selectedSubcategoryId.value = null;
});

// A discarded category/subcategory stays valid on the current assignment but is absent
// from the for_filters list; surface it so the select does not show a bare id.
function withArchivedOption(
  items: Array<{ value: number; title: string }>,
  id: number | null | undefined,
  name: string | null | undefined,
) {
  if (!id || items.some((item) => item.value === id)) return items;

  return [
    { value: id, title: `${name} (${t('patientForm.serviceCategoryArchived')})` },
    ...items,
  ];
}

const serviceCategoryItems = computed(() => withArchivedOption(
  serviceCategories.value.map((category) => ({ value: category.id, title: category.name })),
  initialAssignment?.categoryId,
  initialAssignment?.categoryName,
));

function subcategoryItemsFor(categoryId: number | null) {
  const category = serviceCategories.value.find((candidate) => candidate.id === categoryId);

  return (category?.subcategories ?? []).map((subcategory) => ({
    value: subcategory.id, title: subcategory.name,
  }));
}

const serviceSubcategoryItems = computed(() => {
  const items = subcategoryItemsFor(selectedCategoryId.value);
  if (!initialAssignment || initialAssignment.categoryId !== selectedCategoryId.value) {
    return items;
  }

  return withArchivedOption(items, initialAssignment.subcategoryId,
    initialAssignment.subcategoryName);
});

const assignmentChanged = computed(() =>
  selectedCategoryId.value !== (initialAssignment?.categoryId ?? null) ||
  selectedSubcategoryId.value !== (initialAssignment?.subcategoryId ?? null));

// Only send the assignment when the user actually changed it, so plain patient edits
// never touch the category history.
function serviceAssignmentPayload(): ServiceAssignmentPayload | undefined {
  if (!serviceCategoriesEnabled || !assignmentChanged.value) return undefined;

  return {
    categoryId: selectedCategoryId.value,
    subcategoryId: selectedSubcategoryId.value,
    startsOn: assignmentStartsOn.value || undefined,
  };
}

const { mutate: createPatient, isError: isCreateError, error: createError } = useMutation<Patient, AxiosError>(
  {
    mutationFn: () => patientsApi.create(
      { ...values },
      showGroupPicker ? selectedGroupIds.value : undefined,
      serviceAssignmentPayload(),
    ),
    onSuccess: (patient: Patient) => { window.location.href = `/patients/${patient.id}`; },
    onError: () => { submitButtonDisabled.value = false; },
  },
);

// The RUT belongs to an archived patient: offer "Reactivar" instead of a dead end
// (care-planning companies; the pointer rides in the 400 payload either way).
function archivedPatientFrom(error: AxiosError | null): ArchivedPatientConflict | null {
  const data = error?.response?.data as { archivedPatient?: ArchivedPatientConflict } | undefined;

  return data?.archivedPatient ?? null;
}
const archivedRutConflict = computed<ArchivedPatientConflict | null>(() => (
  statusEnabled && isCreateError.value ? archivedPatientFrom(createError.value) : null
));
const archivedRutConflictMessage = computed(() => {
  const conflict = archivedRutConflict.value;
  if (!conflict) return '';
  const reason = conflict.humanDiscardReason ?
    t('patientForm.archivedRutConflictReason', { reason: conflict.humanDiscardReason }) : '';

  return t('patientForm.archivedRutConflict', {
    name: conflict.fullName, date: formatShortDate(conflict.discardedAt), reason,
  });
});
const isReactivating = ref(false);
const reactivateFailed = ref(false);
function reactivateArchived() {
  const conflict = archivedRutConflict.value;
  if (!conflict) return;
  isReactivating.value = true;
  reactivateFailed.value = false;
  patientsApi.reactivate(conflict.id)
    .then(() => { window.location.href = `/patients/${conflict.id}`; })
    .catch(() => {
      reactivateFailed.value = true;
      isReactivating.value = false;
    });
}

const { mutate: updatePatient, isError: isUpdateError, error: updateError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => patientsApi.update(
      { ...values, id: props.patient?.id },
      showGroupPicker ? selectedGroupIds.value : undefined,
      serviceAssignmentPayload(),
    ),
    onSuccess: () => { window.location.href = `/patients/${props.patient?.id}`; },
    onError: () => { submitButtonDisabled.value = false; },
  },
);

const onSubmit = handleSubmit(() => {
  if (!validateGroupPick()) return;

  submitButtonDisabled.value = true;
  if (props.mode === 'edit') {
    updatePatient();
  } else {
    createPatient();
  }
});

function formErrorMessage(error: AxiosError) {
  const errorData = error.response?.data as {
    type?: string; message?: string; errors?: Record<string, string[]>;
  };
  if (errorData?.type === 'ActiveRecord::RecordNotUnique') {
    return t('patientForm.representativeRecordNotUnique');
  }
  if (errorData?.errors) {
    return Object.values(errorData.errors).flat().join('. ');
  }

  return t('userSession.defaultError');
}

// Closed the name and RUT dialog: the server says what is missing.
function errorMessage(error: AxiosError) {
  return authorIdentityErrorText(error) ?? formErrorMessage(error);
}
</script>
<template>
  <v-container
    class="mx-auto py-4"
    style="max-width: 880px;"
  >
    <go-back-button />
    <h2 class="text-h5 font-weight-medium my-4">
      {{ mode === 'new' ? t('patientForm.createTitle') : t('patientForm.updateTitle') }}
    </h2>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <!-- Personal information -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('patientForm.sectionPersonalInfo') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-row>
            <v-col
              v-if="currentUser?.companyUsesIdCode"
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="idCode"
                v-bind="idCodeAttrs"
                type="number"
                variant="outlined"
                :label="`${t('patient.idCode')}`"
                :error-messages="errors.idCode"
                hide-details="auto"
                @wheel="$event.target.blur()"
                @keypress="isNumber($event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="firstName"
                v-bind="firstNameAttrs"
                variant="outlined"
                :label="`${t('patient.firstName')} (requerido)`"
                :error-messages="errors.firstName"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="lastName"
                v-bind="lastNameAttrs"
                variant="outlined"
                :label="`${t('patient.lastName')} (requerido)`"
                :error-messages="errors.lastName"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="nickname"
                v-bind="nicknameAttrs"
                variant="outlined"
                :label="t('patient.nickname')"
                :error-messages="errors.nickname"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="dni"
                v-bind="dniAttrs"
                variant="outlined"
                :label="t('patient.dni')"
                :error-messages="errors.dni"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="passportNumber"
                v-bind="passportNumberAttrs"
                variant="outlined"
                :label="t('patient.passportNumber')"
                :error-messages="errors.passportNumber"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="birthdate"
                type="date"
                v-bind="birthdateAttrs"
                variant="outlined"
                :label="t('patient.birthdate')"
                :error-messages="errors.birthdate"
                hide-details="auto"
                min="1900-01-01"
                :max="new Date().toISOString().split('T')[0]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="weight"
                v-bind="weightAttrs"
                type="number"
                variant="outlined"
                :label="t('patient.weight')"
                :error-messages="errors.weight"
                hide-details="auto"
                @wheel="$event.target.blur()"
                @keypress="isNumber($event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="d-flex align-center"
            >
              <v-radio-group
                v-model="sex"
                v-bind="sexAttrs"
                :label="t('patient.sex')"
                hide-details="auto"
                inline
              >
                <v-radio
                  :label="t('patientForm.female')"
                  value="female"
                />
                <v-radio
                  :label="t('patientForm.male')"
                  value="male"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Medical fields are stripped together without read_medical_information, so a single
           key check hides the whole section for users who lack the permission. -->
      <v-card
        v-if="'medicalHistory' in props.patient"
        class="mb-6"
      >
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('patientForm.sectionMedicalInfo') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-textarea
            v-if="'medicalHistory' in props.patient"
            v-model="medicalHistory"
            v-bind="medicalHistoryAttrs"
            variant="outlined"
            :label="t('patient.medicalHistory')"
            :error-messages="errors.medicalHistory"
            hide-details="auto"
            class="mb-4"
          />
          <v-textarea
            v-if="'surgicalHistory' in props.patient"
            v-model="surgicalHistory"
            v-bind="surgicalHistoryAttrs"
            variant="outlined"
            :label="t('patient.surgicalHistory')"
            :error-messages="errors.surgicalHistory"
            hide-details="auto"
            class="mb-4"
          />
          <v-textarea
            v-if="'technicalAids' in props.patient"
            v-model="technicalAids"
            v-bind="technicalAidsAttrs"
            variant="outlined"
            :label="t('patient.technicalAids')"
            :error-messages="errors.technicalAids"
            hide-details="auto"
            class="mb-4"
          />
          <v-textarea
            v-if="'insurances' in props.patient"
            v-model="insurances"
            v-bind="insurancesAttrs"
            variant="outlined"
            :label="t('patient.insurances')"
            :error-messages="errors.insurances"
            hide-details="auto"
          />
        </v-card-text>
      </v-card>

      <!-- Administrative (care period + groups + observaciones) -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('patientForm.sectionAdministrative') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="startsOn"
                type="date"
                v-bind="startsOnAttrs"
                variant="outlined"
                :label="t('patient.startsOn')"
                :error-messages="errors.startsOn"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="endsOn"
                type="date"
                v-bind="endsOnAttrs"
                variant="outlined"
                :label="t('patient.endsOn')"
                :error-messages="errors.endsOn"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row
            v-if="serviceCategoriesEnabled"
            class="mt-0"
          >
            <v-col
              cols="12"
              sm="4"
            >
              <v-autocomplete
                v-model="selectedCategoryId"
                :items="serviceCategoryItems"
                variant="outlined"
                clearable
                :label="t('patientForm.serviceCategory')"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="selectedCategoryId && serviceSubcategoryItems.length > 0"
              cols="12"
              sm="4"
            >
              <v-select
                v-model="selectedSubcategoryId"
                :items="serviceSubcategoryItems"
                variant="outlined"
                clearable
                :label="t('patientForm.serviceSubcategory')"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="assignmentChanged"
              cols="12"
              sm="4"
            >
              <v-text-field
                v-model="assignmentStartsOn"
                type="date"
                variant="outlined"
                :label="t('patientForm.serviceCategoryStartsOn')"
                :hint="t('patientForm.serviceCategoryStartsOnHint')"
                persistent-hint
              />
            </v-col>
          </v-row>
          <group-membership-picker
            v-if="showGroupPicker"
            v-model="selectedGroupIds"
            class="mb-4"
            :required="mustPickGroup"
            :items="groupItems"
            :hint="groupPickerHint"
            :error-message="groupPickError"
          />
          <v-textarea
            v-model="comments"
            v-bind="commentsAttrs"
            variant="outlined"
            :label="t('patient.comments')"
            :error-messages="errors.comments"
            hide-details="auto"
            class="mt-4"
          />
        </v-card-text>
      </v-card>

      <!-- Representatives (repeatable) -->
      <patient-representatives-form
        v-model="representatives"
        class="mb-6"
        :errors="errors"
        :field-array-methods="{ push, remove }"
      />

      <!-- Addresses (repeatable) -->
      <patient-addresses-form
        v-model="addresses"
        class="mb-6"
        :errors="errors"
        :field-array-methods="{ push: pushAddress, remove: removeAddress }"
      />

      <v-alert
        v-if="archivedRutConflict"
        class="mb-4 rounded"
        type="warning"
        variant="tonal"
        icon="mdi-archive"
        data-testid="archived-rut-conflict"
      >
        <div class="d-flex align-center flex-wrap ga-3">
          <div>
            <div>{{ archivedRutConflictMessage }}</div>
            <div class="text-caption text-medium-emphasis">
              {{ t('patientForm.reactivateArchivedHint') }}
            </div>
            <div
              v-if="reactivateFailed"
              class="text-caption text-error"
            >
              {{ t('patientForm.reactivateError') }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            v-if="hasPermission('patients', 'update')"
            color="primary"
            variant="flat"
            size="small"
            :loading="isReactivating"
            data-testid="reactivate-archived"
            @click="reactivateArchived"
          >
            {{ t('patientForm.reactivateArchived') }}
          </v-btn>
        </div>
      </v-alert>
      <v-alert
        v-else-if="isCreateError && createError"
        class="mb-4 rounded"
        type="error"
      >
        {{ errorMessage(createError) }}
      </v-alert>
      <v-alert
        v-if="isUpdateError && updateError"
        class="mb-4 rounded"
        type="error"
      >
        {{ errorMessage(updateError) }}
      </v-alert>

      <!-- Sticky action bar: keeps Save reachable on a long form -->
      <div class="patient-form-actions d-flex align-center ga-3 py-3">
        <v-btn
          color="primary"
          size="large"
          type="submit"
          :disabled="submitButtonDisabled"
          :loading="submitButtonDisabled"
        >
          {{ mode === 'new' ? t('patientForm.createSubmit') : t('patientForm.updateSubmit') }}
        </v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<style scoped>
.patient-form-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
